import { isCapitalBookSummaryV2, type CapitalBookSummaryV2 } from "@tradvera/contracts/portfolio-v2";
import {
  MY_PORTFOLIO_NEXT_FORMULA_VERSION,
  MY_PORTFOLIO_NEXT_SCHEMA_VERSION,
  type MyPortfolioNextReadModel,
  type MyPortfolioSourcedMetric,
} from "./contracts.js";

export interface MyPortfolioNextAccount {
  userId: string;
  getIdToken(forceRefresh?: boolean): Promise<string>;
}

export type MyPortfolioNextBook = CapitalBookSummaryV2 & { kind: "OWNED_PRIVATE" | "PAPER" };

export interface MyPortfolioStatementBundleReceipt {
  schemaVersion: "tradvera.my-portfolio.bundle-receipt.v1";
  acceptanceId: string;
  bundleFingerprint: string;
  bookId: string;
  sourceFileCount: 5;
  ownerConfirmed: true;
  financialAuthority: "NONE";
  acceptedAt: string;
  replayed: boolean;
}

export class MyPortfolioNextClientError extends Error {
  constructor(public readonly code: string, public readonly status?: number) {
    super(code);
    this.name = "MyPortfolioNextClientError";
  }
}

export async function getMyPortfolioNextBooks(options: {
  account: MyPortfolioNextAccount;
  booksEndpoint?: string;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
}): Promise<MyPortfolioNextBook[]> {
  const payload = await ownerGet(options.account, normalizeEndpoint(options.booksEndpoint
    ?? "https://api.tradvera.com/v1/admin/portfolio-v2/books"), options.signal, options.fetchImpl ?? fetch);
  if (!object(payload) || payload.ok !== true || !Array.isArray(payload.books)
    || !payload.books.every(isCapitalBookSummaryV2)) invalidResponse();
  const books = payload.books.filter((book): book is MyPortfolioNextBook =>
    book.kind === "OWNED_PRIVATE" || book.kind === "PAPER");
  if (new Set(books.map((book) => book.bookId)).size !== books.length) invalidResponse();
  return books;
}

export async function getMyPortfolioNextReadModel(options: {
  account: MyPortfolioNextAccount;
  book: MyPortfolioNextBook;
  readEndpoint?: string;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
}): Promise<MyPortfolioNextReadModel | null> {
  const endpoint = normalizeEndpoint(options.readEndpoint
    ?? "https://api.tradvera.com/v1/admin/my-portfolio-next/books");
  const payload = await ownerGet(options.account, `${endpoint}/${encodeURIComponent(options.book.bookId)}`,
    options.signal, options.fetchImpl ?? fetch);
  if (!object(payload) || payload.ok !== true) invalidResponse();
  if (payload.portfolio === null) {
    if (!object(payload.book) || payload.book.bookId !== options.book.bookId
      || payload.book.bookKind !== options.book.kind) invalidResponse();
    return null;
  }
  if (!isMyPortfolioNextReadModel(payload.portfolio, options.book)) invalidResponse();
  return payload.portfolio;
}

export async function approveMyPortfolioNextBundle(options: {
  account: MyPortfolioNextAccount;
  book: MyPortfolioNextBook;
  importIds: readonly string[];
  readEndpoint?: string;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
}): Promise<MyPortfolioStatementBundleReceipt> {
  if (options.importIds.length !== 5 || new Set(options.importIds).size !== 5
    || options.importIds.some((id) => typeof id !== "string" || id.length < 3)) {
    throw new MyPortfolioNextClientError("MY_PORTFOLIO_BUNDLE_REQUIRES_FIVE_FILES");
  }
  const endpoint = normalizeEndpoint(options.readEndpoint
    ?? "https://api.tradvera.com/v1/admin/my-portfolio-next/books");
  const token = await options.account.getIdToken();
  const response = await (options.fetchImpl ?? fetch)(
    `${endpoint}/${encodeURIComponent(options.book.bookId)}/bundles/approve`, {
      method: "POST", cache: "no-store", signal: options.signal,
      headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
      body: JSON.stringify({ importIds: options.importIds }),
    });
  let payload: unknown;
  try { payload = await response.json() as unknown; } catch { return invalidResponse(); }
  if (!response.ok) {
    const code = object(payload) && typeof payload.error === "string" ? payload.error
      : object(payload) && typeof payload.code === "string" ? payload.code
        : `MY_PORTFOLIO_NEXT_HTTP_${response.status}`;
    throw new MyPortfolioNextClientError(code, response.status);
  }
  if (!object(payload) || payload.ok !== true || !bundleReceipt(payload.receipt, options.book.bookId)) {
    return invalidResponse();
  }
  return payload.receipt;
}

export function selectCurrentMyPortfolioBook(
  books: readonly MyPortfolioNextBook[],
  kind: MyPortfolioNextBook["kind"],
): MyPortfolioNextBook | null {
  const active = books.filter((book) => book.kind === kind && book.status === "ACTIVE");
  if (active.length > 1) throw new MyPortfolioNextClientError(`MULTIPLE_ACTIVE_${kind}_BOOKS`);
  return active[0] ?? null;
}

export function isMyPortfolioNextReadModel(value: unknown, expected: Pick<MyPortfolioNextBook, "bookId" | "kind">):
value is MyPortfolioNextReadModel {
  if (!object(value) || value.schemaVersion !== MY_PORTFOLIO_NEXT_SCHEMA_VERSION || !iso(value.calculatedAt)
    || !object(value.identity) || value.identity.bookId !== expected.bookId || value.identity.bookKind !== expected.kind
    || typeof value.identity.ownerId !== "string" || typeof value.identity.currency !== "string"
    || !object(value.freshness) || !nullableIso(value.freshness.asOf) || !nullableIso(value.freshness.knownAt)
    || !["CURRENT", "UNKNOWN", "STALE"].includes(String(value.freshness.state))
    || !dataQuality(value.dataQuality) || !summary(value.summary)
    || !Array.isArray(value.holdings) || !value.holdings.every(holding)
    || !tradeHistory(value.tradeHistory) || !behavior(value.behavior)
    || !object(value.importState) || !["EMPTY", "REVIEW_REQUIRED", "READY", "BLOCKED"].includes(String(value.importState.state))
    || (value.importState.bundleFingerprint !== null && typeof value.importState.bundleFingerprint !== "string")
    || !strings(value.importState.sourceFileIds) || !hermes(value.hermes)) return false;
  return true;
}

function summary(value: unknown): boolean {
  return metricRecord(value, ["currentPortfolioValue", "accountBalance", "totalPurchaseValue", "totalMarketValue",
    "realizedPnl", "unrealizedPnl", "totalPnl", "positionsCount", "closedTradesCount", "winRatePct",
    "profitFactor"]);
}

function holding(value: unknown): boolean {
  return object(value) && typeof value.holdingId === "string" && typeof value.instrumentId === "string"
    && typeof value.symbol === "string" && metricRecord(value, ["companyName", "assetClass", "quantity",
      "averagePurchasePrice", "currentPrice", "totalPurchaseValue", "currentValue", "unrealizedPnl",
      "unrealizedPnlPct", "firstBuyAt", "lastBuyAt", "holdingDurationMs", "sector", "positionQuality"]);
}

function tradeHistory(value: unknown): boolean {
  return object(value) && Array.isArray(value.companies) && value.companies.every((item) => object(item)
    && typeof item.instrumentId === "string" && typeof item.symbol === "string" && metricRecord(item,
      ["companyName", "closedTradesCount", "totalQuantity", "averageEntryPrice", "lastExitPrice",
        "totalPurchaseValue", "totalSaleValue", "realizedPnl", "realizedPnlPct", "firstEntryAt", "lastExitAt",
        "averageDurationMs", "sector", "aggregateQuality"]))
    && Array.isArray(value.trades) && value.trades.every((item) => object(item) && typeof item.tradeId === "string"
      && typeof item.instrumentId === "string" && typeof item.symbol === "string"
      && ["LONG", "SHORT"].includes(String(item.direction)) && metricRecord(item, ["companyName", "quantity",
        "entryAt", "exitAt", "entryPrice", "exitPrice", "purchaseValue", "saleValue", "fees", "realizedPnl",
        "realizedPnlPct", "durationMs", "tradeQuality"]) && Array.isArray(item.fills)
      && item.fills.every(fill) && strings(item.learningEvidence));
}

function fill(value: unknown): boolean {
  return object(value) && typeof value.fillId === "string" && ["BUY", "SELL"].includes(String(value.side))
    && finite(value.quantity) && finite(value.price) && iso(value.occurredAt)
    && strings(value.sourceIds) && strings(value.evidenceIds);
}

function behavior(value: unknown): boolean {
  return object(value) && Number.isSafeInteger(value.sampleSize)
    && ["INSUFFICIENT_DATA", "PROVISIONAL_TREND", "RELATIVELY_STABLE_TREND"].includes(String(value.sampleState))
    && metricRecord(value, ["wins", "losses", "winRatePct", "averageWin", "averageLoss", "averageDurationMs",
      "medianDurationMs", "dominantHoldingStyle", "mostTradedSymbol"])
    && nullableString(value.bestTradeId) && nullableString(value.worstTradeId)
    && Array.isArray(value.signals) && value.signals.length === 5 && value.signals.every((signal) => object(signal)
      && ["FEAR", "GREED", "OVERCONFIDENCE", "HESITATION", "REVENGE"].includes(String(signal.signalId))
      && metricRecord(signal, ["score", "frequencyPct", "severityPct", "coveragePct"])
      && Number.isSafeInteger(signal.eligibleDecisionCount) && Number.isSafeInteger(signal.assessedDecisionCount)
      && strings(signal.evidenceTradeIds))
    && object(value.weeklyProgress)
    && ["IMPROVING", "STABLE", "DECLINING", "INSUFFICIENT_DATA"].includes(String(value.weeklyProgress.state))
    && metric(value.weeklyProgress.delta) && strings(value.weeklyProgress.contributingTradeIds);
}

function dataQuality(value: unknown): boolean {
  return object(value) && ["READY", "READY_WITH_WARNINGS", "REVIEW_REQUIRED", "BLOCKED"].includes(String(value.state))
    && nonnegativeInteger(value.sourceFileCount) && nonnegativeInteger(value.sourceRecordCount)
    && nonnegativeInteger(value.recognizedRoleCount) && nonnegativeInteger(value.unknownFeeRecordCount)
    && nonnegativeInteger(value.duplicateExecutionCount) && strings(value.warningCodes) && strings(value.blockingCodes)
    && Array.isArray(value.requiredRoles) && value.requiredRoles.every((item) => object(item)
      && ["POSITIONS", "CASH", "ORDER_HISTORY"].includes(String(item.role)) && typeof item.present === "boolean")
    && Array.isArray(value.files) && value.files.every((file) => object(file) && typeof file.fileId === "string"
      && typeof file.originalName === "string" && typeof file.sha256 === "string" && typeof file.role === "string"
      && nonnegativeInteger(file.recordCount) && ["ACCEPTED", "REVIEW_REQUIRED", "BLOCKED"].includes(String(file.state))
      && strings(file.reasonCodes));
}

function bundleReceipt(value: unknown, bookId: string): value is MyPortfolioStatementBundleReceipt {
  return object(value) && value.schemaVersion === "tradvera.my-portfolio.bundle-receipt.v1"
    && typeof value.acceptanceId === "string" && typeof value.bundleFingerprint === "string"
    && value.bookId === bookId && value.sourceFileCount === 5 && value.ownerConfirmed === true
    && value.financialAuthority === "NONE" && iso(value.acceptedAt) && typeof value.replayed === "boolean";
}

function hermes(value: unknown): boolean {
  return object(value) && ["NOT_REQUESTED", "READY", "REJECTED_UNSUPPORTED_CLAIM"].includes(String(value.state))
    && nullableString(value.narrative) && strings(value.factIds) && strings(value.ruleIds) && strings(value.evidenceIds);
}

function metricRecord(value: unknown, keys: readonly string[]): boolean {
  return object(value) && keys.every((key) => metric(value[key]));
}

function metric(value: unknown): value is MyPortfolioSourcedMetric<unknown> {
  return object(value) && ["AVAILABLE", "KNOWN_ZERO", "UNKNOWN", "STALE", "CONFLICT"].includes(String(value.state))
    && strings(value.sourceIds) && strings(value.evidenceIds) && nullableIso(value.asOf) && nullableIso(value.knownAt)
    && value.formulaVersion === MY_PORTFOLIO_NEXT_FORMULA_VERSION && strings(value.reasonCodes);
}

async function ownerGet(account: MyPortfolioNextAccount, url: string, signal: AbortSignal | undefined,
  fetchImpl: typeof fetch): Promise<unknown> {
  const token = await account.getIdToken();
  const response = await fetchImpl(url, { method: "GET", cache: "no-store", signal,
    headers: { authorization: `Bearer ${token}` } });
  if (!response.ok) throw new MyPortfolioNextClientError(`MY_PORTFOLIO_NEXT_HTTP_${response.status}`, response.status);
  try { return await response.json() as unknown; } catch { return invalidResponse(); }
}

function normalizeEndpoint(value: string): string {
  const trimmed = value.trim().replace(/\/+$/u, "");
  if (!/^https?:\/\//u.test(trimmed)) throw new MyPortfolioNextClientError("MY_PORTFOLIO_NEXT_ENDPOINT_INVALID");
  return trimmed;
}

function object(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}
function strings(value: unknown): value is string[] { return Array.isArray(value) && value.every((item) => typeof item === "string"); }
function nullableString(value: unknown): boolean { return value === null || typeof value === "string"; }
function finite(value: unknown): value is number { return typeof value === "number" && Number.isFinite(value); }
function nonnegativeInteger(value: unknown): boolean { return Number.isSafeInteger(value) && Number(value) >= 0; }
function iso(value: unknown): value is string { return typeof value === "string" && Number.isFinite(Date.parse(value)); }
function nullableIso(value: unknown): boolean { return value === null || iso(value); }
function invalidResponse(): never { throw new MyPortfolioNextClientError("MY_PORTFOLIO_NEXT_RESPONSE_INVALID"); }
